import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';

import { ContextActionType, useDumbActionsContext } from '@europrocurement/flexy-components';
import { ColumnDatatable, FiltersDatatableList } from '@europrocurement/flexy-datatable';
import { CategorieDArticleJsonldCategorieArticleAll } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { domainSelector, useOffreService } from '@europrocurement/l2d-domain';
import { addItem, FaOptionIcon } from '@europrocurement/l2d-icons';
import { castIriToId } from '@europrocurement/l2d-utils';

import RoutedListing from '../components/RoutedListing';
import { getStringColumn } from '../listings';
import useFilterOptions from '../Catalog/useFilterOptions';
import { getStaticSelectFilter } from '../components/filters';
import { articleCategoryCreatePath, articleCategoryFichePath } from './routes';

const Panel: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const { articleCategoryModel: model } = useOffreService();
    const fetchedDomainsOptions = useSelector(domainSelector).main.data;
    const filterOptions = useFilterOptions();

    const goToCreate = React.useCallback(() => navigate(articleCategoryCreatePath), [navigate]);
    const goToFiche = React.useCallback(
        (item: { id: string | number }) => navigate(articleCategoryFichePath(item.id)),
        [navigate],
    );

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: "Créer une nouvelle catégorie d'article",
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: goToCreate,
            },
        ],
        [goToCreate],
    );

    useDumbActionsContext(contextActions);

    const getDomainLabel = React.useCallback(
        (domaine?: string | null): string => {
            if (!domaine) {
                return '(aucun domaine)';
            }
            const domainId = Number(castIriToId(domaine));
            const found = fetchedDomainsOptions.find((domain) => domain.id === domainId);
            return found?.libelle ?? '---';
        },
        [fetchedDomainsOptions],
    );

    const columns: ColumnDatatable<CategorieDArticleJsonldCategorieArticleAll>[] = [
        getStringColumn('libelle', 'Libellé'),
        {
            label: 'Domaine',
            displayLabel: true,
            render: (item) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {getDomainLabel(item.domaine)}
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
            field: 'domaine',
        },
        {
            label: 'Type de validateur',
            displayLabel: true,
            render: (item) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {item.validatorType?.label ?? '---'}
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
            field: 'validatorType',
        },
    ];

    const filters: FiltersDatatableList = [
        getStaticSelectFilter('Domaine', 'domaineId', filterOptions.domains),
    ];

    return (
        <RoutedListing
            listingTitle="Liste des catégories d'articles"
            createButton={{
                label: "Créer une nouvelle catégorie d'article",
                icon: addItem,
                onClick: goToCreate,
            }}
            listingColumns={columns as ColumnDatatable<unknown>[]}
            model={model}
            filters={filters}
            filtersControl
            onRowClick={goToFiche}
        />
    );
};

export default Panel;
